import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action, get } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import { service } from '@ember/service';
import { isNone } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import can from 'ember-can/helpers/can';
import type AbilitiesService from 'ember-can/services/abilities';
import { task } from 'ember-concurrency';
import SuperTextEditor from 'ember-super-prosemirror/components/super-text-editor';
import { eq, or, not } from 'ember-truth-helpers';
import ConfirmDirtyTransition from 'garaje/components/confirm-dirty-transition';
import FeatureOrUpgradeButton from 'garaje/components/feature-or-upgrade-button';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import type Location from 'garaje/models/location';
import NotificationMessage, { type NotificationMessageAttributes } from 'garaje/models/notification-message';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { loadSrcDoc } from 'garaje/utils/script-loader';
import urlBuilder from 'garaje/utils/url-builder';
import { and } from 'macro-decorators';

const FEATURE_NAME = 'custom-notifications';

interface CustomTextNotification {
  name: string;
  description: string;
}

interface MessageGroupVariables {
  type: string;
  data: NotificationData[];
  description: string;
  variables: string[];
}

interface NotificationData {
  name: string;
  description: string;
  for: string;
  template: string;
  default: string;
  optionName: string;
  variables: string[];
  preview: string;
  custom?: boolean;
}

interface VariablePlaceholders {
  visitorName: string;
  visitorEmail: string;
  assistantName: string;
  assistantEmail: string;
  hostName: string;
  hostEmail: string;
  companyName: string;
  modelName: string;
  numberOfVisitors: number;
  carrier: string;
}

interface PreviewEmailObject {
  subject?: string;
  body?: string;
}

interface EmailPreviewTemplate {
  body: string;
  field: string;
}

interface AjaxResponse {
  data: {
    attributes: EmailPreviewTemplate;
  };
}

// Global window extension for srcDoc
declare global {
  interface Window {
    srcDoc: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      set: (iframe: Element, content: any) => unknown;
    };
  }
}

interface CustomTextArgs {
  model: Location;
  feature?: string;
  closeFeature?: () => void;
  openFeature?: (featureName: string) => void;
}

interface CustomTextComponentSignature {
  Args: CustomTextArgs;
  Element: HTMLDivElement;
}

/**
 * Creates notifications objects used to define notification types for the UI
 */
function createCustomTextNotifications(): CustomTextNotification[] {
  return [
    { name: 'hostMessage', description: 'Host notification' },
    { name: 'delegatedMessage', description: 'Assistant notification' },
    { name: 'message', description: 'Fallback notification: sent when a visitor does not select a host' },
  ];
}

/**
 * Creates notifications group objects used to define notification groups for the UI
 */
function createMessageGroups(): MessageGroupVariables[] {
  return [
    {
      type: 'individual',
      data: [],
      description: 'Host notification for single visitor sign-in',
      variables: ['%{visitor_name}', '%{host_name}', '%{company_name}', '%{location_name}'],
    },
    {
      type: 'bulk',
      data: [],
      description: 'Host notification for group sign-in',
      variables: ['%{visitor_name}', '%{host_name}', '%{company_name}', '%{location_name}', '%{number_of_visitors}'],
    },
  ];
}

export default class CustomText extends Component<CustomTextComponentSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare ajaxFetch: AjaxFetchService;
  @service declare abilities: AbilitiesService;
  @service declare state: StateService;

  @tracked isEditingCustomNotification = true;
  @tracked previewEmailObject: PreviewEmailObject = {} as PreviewEmailObject;
  @tracked emailPreviewTemplate: EmailPreviewTemplate | null = null;
  @tracked previewFor: string = 'individual';
  @tracked previewNotificationType: string = 'hostMessage';
  @tracked activeTab = 'Push';
  @tracked showUnsavedEditsWarning = false;
  @tracked isOpen: boolean;

  @and('isOpen', 'isCustomNotificationSubscriber') declare isSettingsPanelOpen: boolean;

  constructor(owner: unknown, args: CustomTextComponentSignature['Args']) {
    super(owner, args);

    this.isOpen = this.args.feature ? this.args.feature === FEATURE_NAME : false;
  }

  get isCustomNotificationSubscriber(): boolean {
    if (!this.canAccessCustomNotifications) return false;
    if (this.abilities.cannot('customize sms for external-notifications')) return false;

    return true;
  }

  get canAccessCustomNotifications(): boolean {
    return !!this.state.features?.canAccessCustomNotifications;
  }

  get isAssistantNotificationPreview(): boolean {
    return this.previewNotificationType === 'delegatedMessage';
  }

  get hasDirtyAttributes(): boolean {
    return 'customNotifications' in this.args.model.changedAttributes();
  }

  get messages(): MessageGroupVariables[] {
    const customNotifications = this.args.model.customNotifications || [];
    const defaultNotifications = this.args.model.defaultNotifications || [];

    return createMessageGroups().map((messageGroup) => {
      const { type } = messageGroup;
      const defaultNotification = defaultNotifications.find((n) => n.for === type);
      const customNotification = customNotifications.find((n) => n.for === type);

      messageGroup.data = createCustomTextNotifications().map((base) => {
        const notification: NotificationData = {
          ...base,
          for: type,
          template: defaultNotification ? (get(defaultNotification, base.name) as string) || '' : '',
          default: defaultNotification ? (get(defaultNotification, base.name) as string) || '' : '',
          optionName: base.description.split(':')[0] || '',
          variables: messageGroup.variables.slice(0),
          preview: '',
        };

        if (type === 'bulk') {
          notification.optionName = `Group ${notification.optionName.toLowerCase()}`;
        }

        if (base.name === 'message') {
          const hostNameIndex = notification.variables.indexOf('%{host_name}');
          if (hostNameIndex > -1) {
            notification.variables.splice(hostNameIndex, 1);
          }
        }

        // NOTE message could be '' which is falsey
        if (customNotification && !isNone(get(customNotification, base.name))) {
          notification.template = (get(customNotification, base.name) as string) || '';
          notification.custom = true;
        }

        const placeholders = this.variablePlaceholders;

        notification.preview = notification.template
          .replace(/%{visitor_name}/g, placeholders.visitorName)
          .replace(/%{host_name}/g, placeholders.hostName)
          .replace(/%{carrier}/g, placeholders.carrier)
          .replace(/%{company_name}/g, placeholders.companyName)
          .replace(/%{location_name}/g, placeholders.modelName)
          .replace(/%{number_of_visitors}/g, placeholders.numberOfVisitors.toString());

        return notification;
      });
      return messageGroup;
    });
  }

  get variablePlaceholders(): VariablePlaceholders {
    const { model } = this.args;

    // Error without Ember.get()
    return {
      visitorName: 'Sophia Fitzroy',
      visitorEmail: 'sophia@email.com',
      assistantName: 'Harry Potter',
      assistantEmail: 'harry@hogwarts.com',
      // eslint-disable-next-line ember/no-get
      hostName: (get(model, 'locationAdmin.fullName') || 'Alexa Beckfield') as VariablePlaceholders['hostName'],
      // eslint-disable-next-line ember/no-get
      hostEmail: (get(model, 'locationAdmin.email') || 'alexa@email.com') as VariablePlaceholders['hostEmail'],
      // eslint-disable-next-line ember/no-get
      companyName: (get(model, 'company.name') || '') as VariablePlaceholders['companyName'],
      modelName: model.name || '',
      numberOfVisitors: 7,
      carrier: 'carrier',
    };
  }

  get currentPreviewMessage(): string | undefined {
    const { previewFor, previewNotificationType, messages } = this;
    const messageGroup = messages.find((m) => m.type === previewFor);
    const { data } = messageGroup || {};
    if (data && data.length) {
      const previewMessage = data.find((d) => d.name === previewNotificationType);
      return previewMessage?.preview;
    }
    return undefined;
  }

  renderAllNotificationTextPreviews(): MessageGroupVariables[] {
    return this.messages;
  }

  updateCustomNotification(message: NotificationMessage | undefined, attrs: NotificationMessageAttributes): void {
    const { model } = this.args;
    const shouldUpdateMessage = Object.keys(attrs).reduce(
      (acc, key) => acc || attrs[key] !== message?.[key as keyof NotificationMessage],
      false,
    );

    if (shouldUpdateMessage) {
      const updatedMessage = Object.freeze(new NotificationMessage({ ...message, ...attrs }));
      const customNotifications = [...(model.customNotifications || [])];

      if (message && customNotifications.includes(message)) {
        const index = customNotifications.indexOf(message);
        customNotifications.splice(index, 1, updatedMessage);
      } else {
        customNotifications.push(updatedMessage);
      }

      this.args.model.customNotifications = customNotifications;
    }
  }

  @action
  customNotificationTextUpdated(notification: NotificationData, template: string): void {
    const props = { ...notification, template, custom: notification.default !== template };
    const customMessage = this.args.model.customNotifications?.find((n) => n.for === props.for);

    this.updateCustomNotification(customMessage, {
      [props.name]: props.custom ? template : null,
      for: props.for,
    });

    Object.assign(notification, props);
  }

  @action
  handleNotificationContentChange(notification: NotificationData, template: string): void {
    this.customNotificationTextUpdated(notification, template);
  }

  saveCustomNotificationsTask = task({ drop: true }, async () => {
    try {
      await this.args.model.save();
      this.flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  });

  @action
  setCustomNotificationPreviewActive(): void {
    this.fetchEmailPreviewTemplate();
    this.renderAllNotificationTextPreviews();
    this.previewFor = 'individual';
    this.previewNotificationType = 'hostMessage';
    this.isEditingCustomNotification = false;
  }

  loadSrcDoc = (): Promise<void> => {
    return loadSrcDoc().then((didLoad) => {
      if (didLoad) {
        const iframe = document.querySelector<HTMLIFrameElement>('.notificationPreviewIframe');
        if (iframe) {
          if (window?.srcDoc?.set) {
            // eslint-disable-next-line ember/use-ember-get-and-set
            window.srcDoc.set(iframe, this.previewEmailObject.body);
          } else {
            // eslint-disable-next-line no-console
            console.warn('window.srcDoc is not available - email preview may not display correctly');
          }
        }
      }
    });
  };

  fetchEmailPreviewTemplate(): void {
    if (this.emailPreviewTemplate) {
      return;
    }

    void this.ajaxFetch
      .request<AjaxResponse>(urlBuilder.v2.emailNotificationPreviewUrl(this.args.model.id), {
        type: 'GET',
      })
      .then((response) => {
        const data: EmailPreviewTemplate = { ...response.data.attributes };
        const parser = new DOMParser();
        const doc = parser.parseFromString(data.body, 'text/html');
        const fieldCssSelector = 'table table table table table td[align="center"] table';

        const fieldTemplate = doc.querySelector(fieldCssSelector);
        if (fieldTemplate) {
          const textNameEl = fieldTemplate.querySelector('.text-name');
          const textLabelEl = fieldTemplate.querySelector('.text-label');
          if (textNameEl) textNameEl.textContent = '{{field_value}}';
          if (textLabelEl) textLabelEl.textContent = '{{field_name}}';
          data.field = fieldTemplate.innerHTML || '';
        } else {
          data.field = '';
        }

        const messageTextEl = doc.querySelector('.notification-message-text');
        if (messageTextEl) messageTextEl.textContent = '{{preview_message}}';

        const emailFieldsEl = doc.querySelector(fieldCssSelector);
        if (emailFieldsEl) {
          emailFieldsEl.classList.add('email-fields');
          emailFieldsEl.innerHTML = '';
        }

        data.body = doc.documentElement.outerHTML;

        this.emailPreviewTemplate = data;
      });
  }

  @action
  onContinue(): void {
    if (this.hasDirtyAttributes) {
      this.args.model.rollbackAttributes();
    }
    this.showUnsavedEditsWarning = false;
    this.changeIsOpen(false);
  }

  @action
  cancel(): void {
    if (!this.hasDirtyAttributes) {
      this.onContinue();
    } else {
      this.showUnsavedEditsWarning = true;
    }
  }

  // When the user changes the notification type in the preview dropdown
  @action
  changePreviewText(typeVal: string): void {
    const [type, notificationType] = typeVal.split('-');
    this.previewFor = type || 'individual';
    this.previewNotificationType = notificationType || 'hostMessage';
  }

  @action
  changeIsOpen(isOpen: boolean): void {
    if (isOpen && this.args.openFeature) {
      this.args.openFeature(FEATURE_NAME);
    } else if (!isOpen && this.hasDirtyAttributes) {
      this.showUnsavedEditsWarning = true;
    }
    this.isOpen = isOpen;
  }

  @action
  enableEditing(): void {
    this.isEditingCustomNotification = true;
  }

  @action
  cancelTransition(): void {
    this.showUnsavedEditsWarning = false;
  }

  @action
  handleEmailPreviewDidChange(): void {
    const previewTemplate = this.emailPreviewTemplate;
    if (!previewTemplate) {
      return;
    }

    const fields = [
      { name: 'Your Full Name', value: this.variablePlaceholders.visitorName },
      { name: 'Your Email Address', value: this.variablePlaceholders.visitorEmail },
      { name: 'Host', value: this.variablePlaceholders.hostName },
    ];

    const parser = new DOMParser();
    const emailDoc = parser.parseFromString(previewTemplate.body, 'text/html');

    const messageTextEl = emailDoc.querySelector('.notification-message-text');
    if (messageTextEl) messageTextEl.textContent = this.currentPreviewMessage || '';

    const emailFieldsEl = emailDoc.querySelector('.email-fields');
    if (emailFieldsEl) {
      const fieldsHtml = fields
        .map((field) => {
          // Create temporary elements to safely escape text content
          const nameDiv = document.createElement('div');
          nameDiv.textContent = field.name;
          const fieldName = nameDiv.innerHTML;

          const valueDiv = document.createElement('div');
          valueDiv.textContent = field.value;
          const fieldValue = valueDiv.innerHTML;

          return previewTemplate.field.replace('{{field_name}}', fieldName).replace('{{field_value}}', fieldValue);
        })
        .join('');
      emailFieldsEl.innerHTML = fieldsHtml;
    }

    this.previewEmailObject = {
      subject: this.currentPreviewMessage || '',
      body: emailDoc.documentElement.outerHTML,
    };
  }

  <template>
    <SettingsPanel
      data-test-notifications-custom-text
      @isOpen={{this.isSettingsPanelOpen}}
      ...attributes
      {{didUpdate this.handleEmailPreviewDidChange this.currentPreviewMessage}}
      {{didUpdate this.handleEmailPreviewDidChange this.emailPreviewTemplate}}
      as |sp|
    >
      {{#let sp.header sp.body sp.bodyStates as |PanelHeader PanelBody PanelStates|}}
        <PanelHeader
          @iconUri="/assets/images/features/custom-host-notifications.svg"
          @title="Host notification text"
          @description="Customize the text that appears in your host notifications."
          @onCancel={{this.cancel}}
          as |header|
        >
          <header.controls>
            {{#if sp.isOpen}}
              <button
                data-test-save-custom-text
                type="button"
                class="btn btn--small btn--brand"
                disabled={{or (not @model.hasDirtyAttributes) this.saveCustomNotificationsTask.isRunning}}
                {{on "click" this.saveCustomNotificationsTask.perform}}
              >
                {{if this.saveCustomNotificationsTask.isRunning "Saving..." "Save"}}
              </button>
            {{else}}
              <FeatureOrUpgradeButton
                @level="premium"
                @upgradeQueryParams={{hash plan="premium" period="monthly"}}
                @isAuthorized={{this.canAccessCustomNotifications}}
                @featureName="custom_notifications"
                @isSlack={{false}}
                @logCTAEvents={{true}}
                @shouldCheckMinLevel={{false}}
                @vrOnly={{false}}
              >
                {{#if (can "customize sms external-notification")}}
                  <button
                    data-test-edit-custom-text
                    type="button"
                    {{on "click" (fn this.changeIsOpen true)}}
                    class="btn btn--small btn--brand"
                  >
                    Edit
                  </button>
                {{else}}
                  <a class="btn btn--small btn--brand" href="javascript:void(0);" onclick="Intercom('show')">
                    Contact us
                  </a>
                {{/if}}
              </FeatureOrUpgradeButton>
            {{/if}}
          </header.controls>
        </PanelHeader>
        <PanelBody class="!overflow-scroll" as |PanelContent|>
          <PanelContent @style="inset" class="mr-4">
            <PanelStates
              @isEditing={{this.isEditingCustomNotification}}
              @toEditing={{this.enableEditing}}
              @isPreviewing={{not this.isEditingCustomNotification}}
              @toPreviewing={{this.setCustomNotificationPreviewActive}}
            />

            {{#if this.isEditingCustomNotification}}
              {{#each this.messages key="type" as |messageGroup|}}
                {{#if messageGroup.data}}
                  <div class="mb-4 w-11/12">
                    <h4 class="mt-16 -mb-2">{{messageGroup.description}}</h4>
                    {{#each messageGroup.data key="name" as |notification|}}
                      <label class="mt-8 mb-1 block">
                        {{notification.description}}
                      </label>
                      <SuperTextEditor
                        {{! @glint-expect-error }}
                        class="custom-host-notifications ember-prose-mirror"
                        @excludeMenuItems="all"
                        @onContentChange={{fn this.handleNotificationContentChange notification}}
                        @defaultValue={{notification.default}}
                        @value={{notification.template}}
                        @variables={{notification.variables}}
                        @docFormat="notification"
                      />
                    {{/each}}
                  </div>
                {{/if}}
              {{/each}}
            {{else}}
              <div class="border-t bg-carbon-5 -mx-4 -mb-4 rounded-b-sm" style="margin-left:calc(-50px - 2rem)">
                <nav class="tabs my-4 mx-8 sidebarNotifications">
                  <button
                    class="text-sm mr-4 py-2 inline-block btn-reset {{if (eq this.activeTab 'Push') 'active'}}"
                    type="button"
                    {{on "click" (fn (mut this.activeTab) "Push")}}
                  >
                    Passport app
                  </button>
                  <button
                    class="text-sm mr-4 py-2 inline-block btn-reset {{if (eq this.activeTab 'Email') 'active'}}"
                    type="button"
                    {{on "click" (fn (mut this.activeTab) "Email")}}
                  >
                    Email
                  </button>
                  <button
                    class="text-sm mr-4 py-2 inline-block btn-reset {{if (eq this.activeTab 'SMS') 'active'}}"
                    type="button"
                    {{on "click" (fn (mut this.activeTab) "SMS")}}
                  >
                    SMS
                  </button>
                  <button
                    class="text-sm mr-4 py-2 inline-block btn-reset {{if (eq this.activeTab 'Slack') 'active'}}"
                    type="button"
                    {{on "click" (fn (mut this.activeTab) "Slack")}}
                  >
                    Slack
                  </button>
                  <button
                    class="text-sm mr-4 py-2 inline-block btn-reset {{if (eq this.activeTab 'Office365') 'active'}}"
                    type="button"
                    {{on "click" (fn (mut this.activeTab) "Office365")}}
                  >
                    Office 365 Groups
                  </button>
                </nav>
                <div class="flex-auto min-w-0 min-h-0 flex flex-col notificationPreviews">
                  <select
                    class="my-8 w-6/12 mx-auto flex-none"
                    {{on "change" (pick "target.value" this.changePreviewText)}}
                  >
                    {{#each this.messages key="type" as |messageGroup|}}
                      {{#each messageGroup.data key="name" as |message|}}
                        <option value="{{messageGroup.type}}-{{message.name}}">{{message.optionName}}</option>
                      {{/each}}
                    {{/each}}
                  </select>

                  {{! PUSH }}
                  <div
                    class="flex-auto min-w-0 min-h-0 px-8 pb-8 notificationPreview notificationPreview-Push
                      {{if (eq this.activeTab 'Push') 'active'}}"
                  >
                    <div class="iphonePreviewWrapper mx-auto">
                      <div class="iphonePreviewCamera"></div>
                      <div class="iphonePreviewSpeaker"></div>
                      <div class="iphoneInnerWrapper hostNotificationPush">
                        <div class="iphonePreview">
                          <strong class="block">{{@model.nameWithCompanyName}}</strong>
                          <span data-test-preview-message>{{this.currentPreviewMessage}}</span>
                        </div>
                      </div>
                      <div class="iphonePreviewHomeButton"></div>
                    </div>
                  </div>

                  {{! EMAIL }}
                  <div
                    class="flex-auto min-w-0 min-h-0 px-8 pb-8 notificationPreview notificationPreview-Email
                      {{if (eq this.activeTab 'Email') 'active'}}"
                  >
                    <div class="emailHtmlHeader">
                      <div class="macStopLights">
                        <span class="text-red-50"></span>
                        <span class="text-papaya-50"></span>
                        <span class="text-cilantro-50"></span>
                      </div>

                      <table class="macMailHeader">
                        <tbody>
                          <tr>
                            <td class="leftSide">From:</td>
                            <td>Envoy <span class="address">&lt;no-reply@envoy.com&gt;</span></td>
                          </tr>
                          <tr>
                            <td class="leftSide">To:</td>
                            <td data-test-to-field>
                              {{#if this.isAssistantNotificationPreview}}
                                {{this.variablePlaceholders.assistantName}}
                                <span class="address">&lt;{{this.variablePlaceholders.assistantEmail}}&gt;</span>
                              {{else}}
                                {{this.variablePlaceholders.hostName}}
                                <span class="address">&lt;{{this.variablePlaceholders.hostEmail}}&gt;</span>
                              {{/if}}
                            </td>
                          </tr>
                          <tr class="subject">
                            <td class="leftSide">Subject:</td>
                            <td>
                              <strong data-test-preview-subject>
                                {{#if this.previewEmailObject}}
                                  {{this.previewEmailObject.subject}}
                                {{else}}
                                  Loading...
                                {{/if}}
                              </strong>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <iframe
                      title="notification-preview"
                      class="emailHtml notificationPreviewIframe"
                      srcdoc={{this.previewEmailObject.body}}
                      {{didInsert this.loadSrcDoc}}
                    ></iframe>
                  </div>

                  {{! SMS }}
                  <div
                    class="flex-auto min-w-0 min-h-0 px-8 pb-8 notificationPreview notificationPreview-SMS
                      {{if (eq this.activeTab 'SMS') 'active'}}"
                  >
                    <div class="iphonePreviewWrapper mx-auto">
                      <div class="iphonePreviewCamera"></div>
                      <div class="iphonePreviewSpeaker"></div>
                      <div class="iphoneInnerWrapper hostNotificationMessages">
                        <div class="iphonePreview">
                          <strong class="block">{{@model.nameWithCompanyName}}</strong>
                          <span data-test-preview-message>{{this.currentPreviewMessage}}</span>
                        </div>
                      </div>
                      <div class="iphonePreviewHomeButton"></div>
                    </div>
                  </div>

                  {{! Slack }}
                  <div
                    class="flex-auto min-w-0 min-h-0 px-8 pb-8 notificationPreview notificationPreview-Slack mx-auto
                      {{if (eq this.activeTab 'Slack') 'active'}}"
                  >
                    <div class="browserHeader">
                      <div class="macStopLights">
                        <span class="text-red-50"></span>
                        <span class="text-papaya-50"></span>
                        <span class="text-cilantro-50"></span>
                      </div>
                    </div>
                    <div class="hostNotificationBrowserWrapper hostNotificationSlack">
                      <div class="hostNotificationTextPreview slackPreview">
                        <div data-test-preview-message>
                          {{this.currentPreviewMessage}}
                        </div>
                        <div>
                          <table class="hostNotificationTextPreview slackPreviewDetails">
                            <tbody>
                              <tr>
                                <td>
                                  <strong class="block">Your Full Name</strong>
                                  <span>{{this.variablePlaceholders.visitorName}}</span>
                                </td>
                                <td>
                                  <strong class="block">Your Email Address</strong>
                                  <span>{{this.variablePlaceholders.visitorEmail}}</span>
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <strong class="block">Host</strong>
                                  <span>{{this.variablePlaceholders.hostName}}</span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  {{! Office365 }}
                  <div
                    class="flex-auto min-w-0 min-h-0 px-8 pb-8 notificationPreview notificationPreview-Office365 mx-auto
                      {{if (eq this.activeTab 'Office365') 'active'}}"
                  >
                    <div class="browserHeader">
                      <div class="macStopLights">
                        <span class="text-red-50"></span>
                        <span class="text-papaya-50"></span>
                        <span class="text-cilantro-50"></span>
                      </div>
                    </div>
                    <div class="hostNotificationBrowserWrapper hostNotificationOffice365">
                      <div class="hostNotificationTextPreview office365Preview-header">
                        <div data-test-preview-message>
                          {{this.currentPreviewMessage}}
                        </div>
                      </div>
                      <div class="hostNotificationTextPreview office365Preview">
                        <div data-test-preview-message>
                          {{this.currentPreviewMessage}}
                        </div>
                        <table class="hostNotificationTextPreview office365PreviewDetails">
                          <tbody>
                            <tr>
                              <td style="padding: 2px 0;">
                                <span class="block">Your Full Name</span>
                                <span class="block">Your Email Address</span>
                                <span class="block">Host</span>
                              </td>
                              <td style="padding: 2px 0;">
                                <span class="block">{{this.variablePlaceholders.visitorName}}</span>
                                <span class="block">{{this.variablePlaceholders.visitorEmail}}</span>
                                <span class="block">{{this.variablePlaceholders.hostName}}</span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            {{/if}}

          </PanelContent>
        </PanelBody>
      {{/let}}
    </SettingsPanel>
    {{#if this.showUnsavedEditsWarning}}
      <ConfirmDirtyTransition
        @headerMessage="You have unsaved changes"
        @bodyMessage="You will lose any unsaved changes. Are you sure you want to continue?"
        @confirm={{this.onContinue}}
        @cancel={{this.cancelTransition}}
      />
    {{/if}}
  </template>
}
